import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useAuth } from '@/hooks/useAuth';
import { toast } from '@/hooks/useToast';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { Edit, Eye, EyeOff, Plus, Search, Trash2, X } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

interface User {
  _id: string;
  name: string;
  email: string;
  contactNo: string;
  role: string;
  status: 'ACTIVE' | 'INACTIVE';
  birthDate?: string;
  joiningDate?: string;
  kycType?: string;
  kycNo?: string;
  createdAt: string;
  updatedAt: string;
}

const userSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  email: z.string().email('Please enter a valid email address'),
  contactNo: z.string().min(1, 'Contact number is required'),
  role: z.string().min(1, 'Role is required'),
  password: z.string().min(8, 'Password must be at least 8 characters').optional(),
  status: z.enum(['ACTIVE', 'INACTIVE']),
  birthDate: z.string().optional(),
  joiningDate: z.string().optional(),
  kycType: z.string().optional(),
  kycNo: z.string().optional(),
});

type UserFormData = z.infer<typeof userSchema>;

const UserManagement = () => {
  const { user: currentUser } = useAuth();
  const queryClient = useQueryClient();
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRole, setSelectedRole] = useState<string>('all');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  const userRoles = [
    'BILLING',
    'CUSTOMER_SERVICES',
    'DATA_ENTRY',
    'DELIVERY_BOY',
    'DIRECTOR',
    'MANAGER',
    'OPERATION',
    'PICK_UP_BOY',
    'SALES',
    'SUPERADMIN',
    'SUPERVISOR',
  ];

  const kycTypes = [
    'Aadhaar Card',
    'PAN Card',
    'Passport',
    'Driving License',
    'Voter ID',
    'Other Government ID',
  ];

  const pageSizeOptions = [10, 20, 50];

  // Debounce search input to avoid a request on every keypress
  useEffect(() => {
    const timer = window.setTimeout(() => setSearchTerm(searchInput), 300);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedRole, selectedStatus]);

  useEffect(() => {
    if (!isDialogOpen) {
      setShowPassword(false);
    }
  }, [isDialogOpen]);

  // Fetch users
  const { data: usersResponse, isLoading, isFetching } = useQuery({
    queryKey: ['users', searchTerm, selectedRole, selectedStatus, currentPage, pageSize],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append('page', currentPage.toString());
      params.append('limit', pageSize.toString());
      const normalizedSearch = searchTerm.trim();
      if (normalizedSearch) params.append('search', normalizedSearch);
      if (selectedRole && selectedRole !== 'all') params.append('role', selectedRole);
      if (selectedStatus && selectedStatus !== 'all') params.append('status', selectedStatus);
      
      const response = await axios.get(`/api/users?${params.toString()}`);
      return response.data.data; // This returns the paginated response
    },
  });

  // Extract users array from paginated response
  const users = usersResponse?.data || [];
  const pagination = usersResponse?.pagination;
  const totalUsers = pagination?.total ?? users.length;
  const totalPages = pagination?.totalPages && pagination.totalPages > 0 ? pagination.totalPages : 1;
  const hasNextPage = pagination?.hasNext ?? currentPage < totalPages;
  const hasPrevPage = pagination?.hasPrev ?? currentPage > 1;

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const activeFilters = useMemo(() => {
    const filters: { key: string; label: string; value: string; onClear: () => void }[] = [];
    const normalizedSearch = searchTerm.trim();
    if (normalizedSearch) {
      filters.push({
        key: 'search',
        label: 'Search',
        value: normalizedSearch,
        onClear: () => {
          setSearchInput('');
          setSearchTerm('');
        },
      });
    }
    if (selectedRole !== 'all') {
      filters.push({
        key: 'role',
        label: 'Role',
        value: selectedRole,
        onClear: () => setSelectedRole('all'),
      });
    }
    if (selectedStatus !== 'all') {
      filters.push({
        key: 'status',
        label: 'Status',
        value: selectedStatus === 'ACTIVE' ? 'Active' : 'Inactive',
        onClear: () => setSelectedStatus('all'),
      });
    }
    return filters;
  }, [searchTerm, selectedRole, selectedStatus]);

  const hasActiveFilters = activeFilters.length > 0;

  const form = useForm<UserFormData>({
    resolver: zodResolver(userSchema),
    defaultValues: {
      name: '',
      email: '',
      contactNo: '',
      role: '',
      password: '',
      status: 'ACTIVE',
      birthDate: '',
      joiningDate: '',
      kycType: '',
      kycNo: '',
    },
  });

  // Create user mutation
  const createUserMutation = useMutation({
    mutationFn: async (data: UserFormData) => {
      const response = await axios.post('/api/users', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast({
        title: 'User created',
        description: 'User has been created successfully.',
      });
      setIsDialogOpen(false);
      form.reset();
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.response?.data?.error?.message || 'Failed to create user',
        variant: 'destructive',
      });
    },
  });

  // Update user mutation
  const updateUserMutation = useMutation({
    mutationFn: async (data: UserFormData & { _id: string }) => {
      const { _id, ...updateData } = data;
      const response = await axios.put(`/api/users/${_id}`, updateData);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast({
        title: 'User updated',
        description: 'User has been updated successfully.',
      });
      setIsDialogOpen(false);
      setEditingUser(null);
      form.reset();
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.response?.data?.error?.message || 'Failed to update user',
        variant: 'destructive',
      });
    },
  });

  // Delete user mutation
  const deleteUserMutation = useMutation({
    mutationFn: async (userId: string) => {
      await axios.delete(`/api/users/${userId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast({
        title: 'User deleted',
        description: 'User has been deleted successfully.',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.response?.data?.error?.message || 'Failed to delete user',
        variant: 'destructive',
      });
    },
  });

  const onSubmit = (data: UserFormData) => {
    if (editingUser) {
      const { password, ...updateData } = data;
      updateUserMutation.mutate({ ...updateData, _id: editingUser._id });
    } else {
      if (!data.password?.trim()) {
        form.setError('password', {
          type: 'manual',
          message: 'Password is required',
        });
        return;
      }
      createUserMutation.mutate({ ...data, password: data.password.trim() });
    }
  };

  const handleEdit = (user: User) => {
    setEditingUser(user);
    form.reset({
      name: user.name,
      email: user.email,
      contactNo: user.contactNo,
      role: user.role,
      status: user.status,
      birthDate: user.birthDate ? new Date(user.birthDate).toISOString().split('T')[0] : '',
      joiningDate: user.joiningDate ? new Date(user.joiningDate).toISOString().split('T')[0] : '',
      kycType: user.kycType || '',
      kycNo: user.kycNo || '',
    });
    setIsDialogOpen(true);
  };

  const handleDelete = (userId: string) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      deleteUserMutation.mutate(userId);
    }
  };

  const handleAddNew = () => {
    setEditingUser(null);
    form.reset();
    setIsDialogOpen(true);
  };

  const handleClearFilters = () => {
    setSearchInput('');
    setSearchTerm('');
    setSelectedRole('all');
    setSelectedStatus('all');
  };

  const handlePageSizeChange = (value: string) => {
    const nextSize = Number(value);
    if (!Number.isNaN(nextSize)) {
      setPageSize(nextSize);
      setCurrentPage(1);
    }
  };

  const isLoading_mutation = createUserMutation.isPending || updateUserMutation.isPending;

  // Check if current user has permission to manage users
  if (!currentUser || !['SUPERADMIN', 'MANAGER', 'DIRECTOR'].includes(currentUser.role)) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-muted-foreground">You don't have permission to access this page.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold">User Management</h1>
          <p className="text-muted-foreground">Manage system users and their permissions</p>
        </div>
        <Button onClick={handleAddNew} className="w-full sm:w-auto">
          <Plus className="mr-2 h-4 w-4" />
          Add User
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle className="flex flex-wrap items-center justify-between gap-2">
            <span>Filters</span>
            {hasActiveFilters && (
              <span className="text-sm text-muted-foreground">
                {activeFilters.length} active
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div className="space-y-2">
              <Label>Search</Label>
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search users..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') setSearchTerm(searchInput);
                    if (e.key === 'Escape') {
                      setSearchInput('');
                      setSearchTerm('');
                    }
                  }}
                  className="pl-8 pr-16"
                />
                <div className="absolute right-2 top-2.5 flex items-center gap-1">
                  {searchInput.trim() && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6"
                      onClick={() => {
                        setSearchInput('');
                        setSearchTerm('');
                      }}
                      aria-label="Clear search"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                  {isFetching && <LoadingSpinner size="sm" />}
                </div>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Role</Label>
              <Select value={selectedRole} onValueChange={setSelectedRole}>
                <SelectTrigger>
                  <SelectValue placeholder="All roles" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All roles</SelectItem>
                  {userRoles.map((role) => (
                    <SelectItem key={role} value={role}>
                      {role}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                <SelectTrigger>
                  <SelectValue placeholder="All statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  <SelectItem value="ACTIVE">Active</SelectItem>
                  <SelectItem value="INACTIVE">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <Button
                variant="outline"
                onClick={handleClearFilters}
                disabled={!hasActiveFilters}
                className="w-full"
              >
                Clear Filters
              </Button>
            </div>
          </div>
          {hasActiveFilters && (
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <span className="text-sm text-muted-foreground">Active filters:</span>
              {activeFilters.map((filter) => (
                <Badge
                  key={filter.key}
                  variant="secondary"
                  className="flex items-center gap-1 pr-1"
                >
                  <span className="max-w-[160px] truncate">
                    {filter.label}: {filter.value}
                  </span>
                  <button
                    type="button"
                    onClick={filter.onClear}
                    className="rounded-full p-0.5 text-muted-foreground hover:text-foreground"
                    aria-label={`Clear ${filter.label} filter`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </Badge>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Users Table */}
      <Card>
        <CardHeader className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <CardTitle>Users ({totalUsers})</CardTitle>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            {isFetching && !isLoading && <LoadingSpinner size="sm" />}
            <span>
              {totalUsers > 0 ? `Page ${currentPage} of ${totalPages}` : 'No users found'}
            </span>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center h-32">
              <LoadingSpinner size="lg" />
            </div>
          ) : (
            <>
              <div className="hidden md:block">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Contact</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Joining Date</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users.map((user: User) => (
                      <TableRow key={user._id}>
                        <TableCell className="font-medium">{user.name}</TableCell>
                        <TableCell>{user.email}</TableCell>
                        <TableCell>{user.contactNo}</TableCell>
                        <TableCell>
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {user.role}
                          </span>
                        </TableCell>
                        <TableCell>
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              user.status === 'ACTIVE'
                                ? 'bg-green-100 text-green-800'
                                : 'bg-red-100 text-red-800'
                            }`}
                          >
                            {user.status}
                          </span>
                        </TableCell>
                        <TableCell>
                          {user.joiningDate
                            ? new Date(user.joiningDate).toLocaleDateString()
                            : '-'}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center space-x-2">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleEdit(user)}
                              aria-label={`Edit ${user.name}`}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            {user._id !== currentUser._id && (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleDelete(user._id)}
                                className="text-destructive hover:text-destructive"
                                aria-label={`Delete ${user.name}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                    {users.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center text-muted-foreground">
                          No users found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>

              <div className="space-y-3 md:hidden">
                {users.map((user: User) => (
                  <div
                    key={user._id}
                    className="rounded-lg border border-border bg-card p-4 shadow-sm"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <h3 className="font-semibold text-base">{user.name}</h3>
                        <p className="text-sm text-muted-foreground">{user.email}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleEdit(user)}
                          aria-label={`Edit ${user.name}`}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        {user._id !== currentUser._id && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDelete(user._id)}
                            className="text-destructive hover:text-destructive"
                            aria-label={`Delete ${user.name}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {user.role}
                      </span>
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          user.status === 'ACTIVE'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {user.status}
                      </span>
                    </div>
                    <div className="mt-4 grid gap-2 text-sm">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-muted-foreground">Contact</span>
                        <span className="font-medium">{user.contactNo || '-'}</span>
                      </div>
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-muted-foreground">Joining Date</span>
                        <span className="font-medium">
                          {user.joiningDate
                            ? new Date(user.joiningDate).toLocaleDateString()
                            : '-'}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
                {users.length === 0 && (
                  <div className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground">
                    No users found
                  </div>
                )}
              </div>

              <div className="mt-4 flex flex-col gap-3 border-t pt-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="text-sm text-muted-foreground">
                  Showing {users.length} of {totalUsers} users
                </div>
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">Rows</span>
                    <Select value={String(pageSize)} onValueChange={handlePageSizeChange}>
                      <SelectTrigger className="h-8 w-[90px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {pageSizeOptions.map((size) => (
                          <SelectItem key={size} value={String(size)}>
                            {size}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                      disabled={!hasPrevPage || isFetching}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
                      disabled={!hasNextPage || isFetching}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit User Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingUser ? 'Edit User' : 'Add New User'}
            </DialogTitle>
            <DialogDescription>
              {editingUser
                ? 'Update user information and permissions.'
                : 'Create a new user account with appropriate permissions.'}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Full Name</Label>
                <Input
                  {...form.register('name')}
                  placeholder="Enter full name"
                  disabled={isLoading_mutation}
                />
                {form.formState.errors.name && (
                  <p className="text-sm text-destructive">
                    {form.formState.errors.name.message}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email Address</Label>
                <Input
                  {...form.register('email')}
                  type="email"
                  placeholder="Enter email address"
                  disabled={isLoading_mutation}
                />
                {form.formState.errors.email && (
                  <p className="text-sm text-destructive">
                    {form.formState.errors.email.message}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="contactNo">Contact Number</Label>
                <Input
                  {...form.register('contactNo')}
                  placeholder="Enter contact number"
                  disabled={isLoading_mutation}
                />
                {form.formState.errors.contactNo && (
                  <p className="text-sm text-destructive">
                    {form.formState.errors.contactNo.message}
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="role">Role</Label>
                <Select
                  value={form.watch('role')}
                  onValueChange={(value) => form.setValue('role', value)}
                  disabled={isLoading_mutation}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  <SelectContent>
                    {userRoles.map((role) => (
                      <SelectItem key={role} value={role}>
                        {role}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {form.formState.errors.role && (
                  <p className="text-sm text-destructive">
                    {form.formState.errors.role.message}
                  </p>
                )}
              </div>

              {!editingUser && (
                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <div className="relative">
                    <Input
                      {...form.register('password')}
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Enter password"
                      disabled={isLoading_mutation}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                      onClick={() => setShowPassword(!showPassword)}
                      disabled={isLoading_mutation}
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                  {form.formState.errors.password && (
                    <p className="text-sm text-destructive">
                      {form.formState.errors.password.message}
                    </p>
                  )}
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="status">Status</Label>
                <Select
                  value={form.watch('status')}
                  onValueChange={(value: 'ACTIVE' | 'INACTIVE') => form.setValue('status', value)}
                  disabled={isLoading_mutation}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ACTIVE">Active</SelectItem>
                    <SelectItem value="INACTIVE">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="birthDate">Birth Date</Label>
                <Input
                  {...form.register('birthDate')}
                  type="date"
                  disabled={isLoading_mutation}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="joiningDate">Joining Date</Label>
                <Input
                  {...form.register('joiningDate')}
                  type="date"
                  disabled={isLoading_mutation}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="kycType">KYC Type</Label>
                <Select
                  value={form.watch('kycType')}
                  onValueChange={(value) => form.setValue('kycType', value)}
                  disabled={isLoading_mutation}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select KYC type" />
                  </SelectTrigger>
                  <SelectContent>
                    {kycTypes.map((type) => (
                      <SelectItem key={type} value={type}>
                        {type}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="kycNo">KYC Number</Label>
                <Input
                  {...form.register('kycNo')}
                  placeholder="Enter KYC number"
                  disabled={isLoading_mutation}
                />
              </div>
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsDialogOpen(false)}
                disabled={isLoading_mutation}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isLoading_mutation}>
                {isLoading_mutation && <LoadingSpinner size="sm" className="mr-2" />}
                {editingUser ? 'Update User' : 'Create User'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UserManagement;
